/*
 * MCC card gallery
 * Swipe, drag and trackpad scroll through the photos of a product card, with an edge bounce on the
 * first and last photo and arrows on hover (desktop).
 * Settings: Theme settings > Product cards > Photo gallery on cards.
 *
 * Physics: Embla Carousel 8.6.0 (MIT License, Copyright (c) 2019 David Jerleke) and
 * embla-carousel-wheel-gestures 8.1.0 (MIT License, Copyright (c) Felix Leupold), loaded only when a
 * gallery comes near the screen. Assets: mcc-embla.js, mcc-embla-wheel.js.
 */
(() => {
  if (customElements.get('mcc-card-gallery')) return;

  const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
  const phoneLayout = window.matchMedia('(max-width: 767.98px)');
  const WIDTHS = [180, 360, 540, 720, 900, 1080, 1440];
  let libraryPromise = null;

  const loadScript = (src) =>
    new Promise((resolve, reject) => {
      if (!src) {
        reject(new Error('missing script url'));
        return;
      }
      const script = document.createElement('script');
      script.src = src;
      script.async = true;
      script.onload = resolve;
      script.onerror = reject;
      document.head.appendChild(script);
    });

  const loadLibrary = () => {
    if (!libraryPromise) {
      libraryPromise = (window.EmblaCarousel ? Promise.resolve() : loadScript(window.MCC_EMBLA))
        .then(() => {
          if (window.EmblaCarouselWheelGestures || !finePointer.matches) return null;
          return loadScript(window.MCC_EMBLA_WHEEL).catch(() => null);
        })
        .then(() => {
          if (!window.EmblaCarousel) throw new Error('Embla Carousel not available');
        });
    }
    return libraryPromise;
  };

  // Start a gallery shortly before its card scrolls into view.
  const initObserver =
    'IntersectionObserver' in window
      ? new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) return;
              initObserver.unobserve(entry.target);
              entry.target.init();
            });
          },
          { rootMargin: '300px 0px' }
        )
      : null;

  // A card that stays well in view for a moment gets its second photo ready before the first swipe.
  const preloadObserver =
    'IntersectionObserver' in window
      ? new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              const gallery = entry.target;
              clearTimeout(gallery.preloadTimer);
              if (!entry.isIntersecting) return;
              gallery.preloadTimer = setTimeout(() => {
                preloadObserver.unobserve(gallery);
                gallery.load(1);
              }, 600);
            });
          },
          { threshold: 0.6 }
        )
      : null;

  class MccCardGallery extends HTMLElement {
    connectedCallback() {
      this.slides = Array.from(this.querySelectorAll('[data-mcc-cg-slide]'));
      if (this.slides.length < 2) return;

      this.viewport = this.querySelector('[data-mcc-cg-viewport]');
      this.prevButton = this.querySelector('[data-mcc-cg-prev]');
      this.nextButton = this.querySelector('[data-mcc-cg-next]');
      this.dots = Array.from(this.querySelectorAll('[data-mcc-cg-dot]'));

      // On a touch screen inside a product slider or a sideways swipe row, the swipe belongs to the row:
      // the card keeps its first photo and hands the gesture back to the row.
      const inRow = this.closest('.swiper') || (this.closest('.swipe-mobile') && phoneLayout.matches);
      if (!finePointer.matches && inRow) {
        this.makeStatic();
        return;
      }

      this.onFirstHover = () => this.load(1);
      this.addEventListener('pointerenter', this.onFirstHover, { once: true });

      if (initObserver) initObserver.observe(this);
      else this.init();
      if (preloadObserver) preloadObserver.observe(this);
    }

    disconnectedCallback() {
      if (initObserver) initObserver.unobserve(this);
      if (preloadObserver) preloadObserver.unobserve(this);
      clearTimeout(this.preloadTimer);
      if (this.embla) {
        this.embla.destroy();
        this.embla = null;
      }
      this.starting = false;
    }

    makeStatic() {
      this.classList.add('is-static');
      if (this.viewport) this.viewport.classList.remove('swiper-no-swiping');
    }

    // Photos after the first one load on demand: on hover, when a swipe starts, and one ahead of the current photo.
    // The markup only carries the CDN base URL, so the srcset is built here.
    load(index) {
      const slide = this.slides && this.slides[index];
      if (!slide) return;
      const img = slide.querySelector('img[data-src]');
      if (!img) return;
      const base = img.dataset.src;
      const natural = parseInt(img.dataset.w, 10) || 0;
      const join = base.indexOf('?') === -1 ? '?' : '&';
      const widths = WIDTHS.filter((w) => !natural || w <= natural);
      img.addEventListener('load', () => img.classList.add('is-loaded'), { once: true });
      img.removeAttribute('data-src');
      if (this.dataset.sizes) img.sizes = this.dataset.sizes;
      if (widths.length) {
        img.srcset = widths.map((w) => `${base}${join}width=${w} ${w}w`).join(', ');
        img.src = `${base}${join}width=${widths[Math.min(3, widths.length - 1)]}`;
      } else {
        img.src = base;
      }
      if (img.complete && img.naturalWidth) img.classList.add('is-loaded');
    }

    init() {
      if (this.embla || this.starting) return;
      this.starting = true;

      loadLibrary()
        .then(() => {
          if (!this.isConnected || this.embla) return;
          const plugins = [];
          if (finePointer.matches && window.EmblaCarouselWheelGestures) {
            plugins.push(window.EmblaCarouselWheelGestures());
          }

          this.embla = window.EmblaCarousel(
            this.viewport,
            { align: 'start', containScroll: 'trimSnaps', duration: 24, dragThreshold: 6 },
            plugins
          );

          this.embla.on('select', () => this.update());
          this.embla.on('pointerDown', () => {
            this.classList.add('is-dragging');
            this.load(this.embla.selectedScrollSnap() + 1);
          });
          this.embla.on('pointerUp', () => this.classList.remove('is-dragging'));

          const step = (direction) => (event) => {
            event.preventDefault();
            event.stopPropagation();
            if (direction < 0) this.embla.scrollPrev();
            else this.embla.scrollNext();
          };
          if (this.prevButton) this.prevButton.addEventListener('click', step(-1));
          if (this.nextButton) this.nextButton.addEventListener('click', step(1));

          this.classList.add('is-ready');
          this.update();
        })
        .catch(() => {
          // Without the library the card simply shows its first photo, exactly like before.
          this.makeStatic();
        })
        .finally(() => {
          this.starting = false;
        });
    }

    update() {
      if (!this.embla) return;
      const index = this.embla.selectedScrollSnap();
      this.load(index);
      this.load(index + 1);

      this.slides.forEach((slide, n) => slide.setAttribute('aria-hidden', n === index ? 'false' : 'true'));
      this.dots.forEach((dot, n) => dot.classList.toggle('is-active', n === index));
      if (this.prevButton) this.prevButton.disabled = !this.embla.canScrollPrev();
      if (this.nextButton) this.nextButton.disabled = !this.embla.canScrollNext();
    }
  }

  customElements.define('mcc-card-gallery', MccCardGallery);
})();
